import Scene from '../../scene';
import { MpSdk } from 'src/types';
import type { SDKConfig } from 'src/service/types';
import { gltfLoaderComponentType, makeGLTFLoaderComponent } from 'src/sdk-components';
import { LoadingState, TSceneNode } from 'src/service/scene/types';
import { controllerButtonType } from 'src/sdk-components/ControllerButtonComponent';
import { ComponentInteractionType } from 'src/sdk-components/SceneComponent';
import ThreeInstance from 'src/sdk-components/ThreeInstance';
import { CONTROLLER_CONSTANTS } from 'src/service/scene/scene-nodes/SceneController/constants';
import { NodeEventsTypes } from 'src/service/scene/enums';
import {
  animateCenter,
  animateDown,
  animateUp,
} from 'src/service/scene/scene-nodes/SceneController/utils/modelAnimations';
import {
  setResetButtonPosition,
  setResetSwitchPosition,
} from 'src/service/scene/scene-nodes/SceneController/utils/positionModels';
import { SceneQuiz } from 'src/service/scene/scene-nodes/SceneController/utils/SceneQuiz';
import { transformQuizData } from 'src/service/scene/scene-nodes/SceneController/utils/transformQuizData';
import { UIController } from 'src/service/scene/scene-nodes/SceneController/UIController';
import { Button } from 'src/service/scene/scene-nodes/SceneController/Button';

export enum GamepadDirection {
  UP = 'up',
  DOWN = 'down',
  CENTER = 'center',
}

class SceneController extends Scene {
  readonly mpSDK: any;

  context: any;

  // prevents interaction with component
  active = false;

  assets: Array<any> = [];

  targetIncrease: any;

  targetDecrease: any;

  forceBlockResult = false;

  value = 0.5;

  THREE: any;

  clock: any;

  sweep: any;

  audio: any;

  increase: any = {
    controllerBtn: null,
    target: {
      component: null,
      updateCount: 0,
    },
  };

  decrease: any = {
    controllerBtn: null,
    target: {
      component: null,
      updateCount: 0,
    },
  };

  leverComponent: any;

  resetSwitch: Button;

  resetBtn: Button;

  // videos affected by value of controller
  result: any = {
    early: null,
    late: null,
    ok: null,
  };

  quiz1: any;

  quizEarly: any;

  quizLate: any;

  quizCompleted: any;

  UILeverController: UIController;

  paperMachineVideoAssets: any = [];

  startOutroVideo: any;

  completedImage: any;

  xr: {
    controllerL: any;
    gamepadL: any;
    gamepadLThumbstickDirection;
    controllerR: any;
    gamepadR: any;
    gamepadRThumbstickActive;
  } = {
    controllerL: null,
    gamepadL: null,
    gamepadLThumbstickDirection: GamepadDirection.CENTER,
    controllerR: null,
    gamepadR: null,
    gamepadRThumbstickActive: false,
  };

  //@ts-ignore
  constructor(
    mpSDK: MpSdk,
    private config: SDKConfig,
    private transformControls: any,
  ) {
    super(mpSDK);
    this.mpSDK = mpSDK;
  }

  async init() {
    this.THREE = await ThreeInstance.get();
    await this.mpSDK.Scene.register(gltfLoaderComponentType, makeGLTFLoaderComponent);
  }

  // Init methods

  async create(data: any): Promise<TSceneNode | undefined> {
    return new Promise(async (resolve) => {
      try {
        this.mpSDK.Sweep.current.subscribe((sweep) => {
          if (sweep.id === 'c1c38440428f485d9487045fa0ba9cf4' && this.UILeverController)
            this.UILeverController.setVisible(true);
          if (sweep.id !== 'c1c38440428f485d9487045fa0ba9cf4' && this.UILeverController)
            this.UILeverController.setVisible(false);
          if (this.paperMachineVideoAssets.length) {
            const paperRolling = this.paperMachineVideoAssets.find(
              (asset) => asset.details.title === 'Paper rolling',
            );
            const frontOfPlates = this.paperMachineVideoAssets.find(
              (asset) => asset.details.title === 'Front of plates',
            );
            const frontOfPlatesSquished = this.paperMachineVideoAssets.find(
              (asset) => asset.details.title === 'Front of plates squished',
            );
            paperRolling.node.root.visible = sweep.sid === '15261dd46e064bf090c52ab47c7ebb41';
            frontOfPlates.node.pivot.visible = sweep.sid === '9a99b149ee994ccab2b6dfe1ee00db8d';
            frontOfPlatesSquished.node.pivot.visible =
              sweep.sid === '9a99b149ee994ccab2b6dfe1ee00db8d';
            this.increase.target.component.node.root.visible =
              sweep.sid === 'c1c38440428f485d9487045fa0ba9cf4';
            this.decrease.target.component.node.root.visible =
              sweep.sid === 'c1c38440428f485d9487045fa0ba9cf4';
          }
        });
        this.context = data.context;
        this.hideSweeps(CONTROLLER_CONSTANTS.SWEEPS_TO_HIDE);
        this.increase.target.component = data.targetIncrease;
        this.decrease.target.component = data.targetDecrease;
        this.result = data.result;
        this.paperMachineVideoAssets = data.paperMachineVideoAssets;
        this.startOutroVideo = data.startOutroVideo;
        this.completedImage = data.completedImage;
        this.initQuizes(data);
        this.decrease.target.component.node.material.opacity = 0;

        this.audio = new Audio('/audio/machine_warning.mp3');

        this.clock = new this.THREE.Clock();

        // saving the asset into the array to prevent rendering it few times
        const modelNode = await this.mpSDK.Scene.createNode();

        const [leverComponent, UILeverController, resetControllers] = await Promise.all([
          this.createLever(modelNode),
          this.createUILeverController(modelNode),
          this.createResetControllers(modelNode),
        ]);

        this.leverComponent = leverComponent;

        this.UILeverController = UILeverController;
        this.resetBtn = resetControllers.resetBtn;
        this.resetSwitch = resetControllers.resetSwitch;

        modelNode.start();

        const assetsLength = this.assets.push([data, []]);
        leverComponent.outputs.onPropertyChanged('loadingState', async (state: any) => {
          if (state === LoadingState.Loaded) {
            leverComponent.root.position.x = 5.232;
            leverComponent.root.position.y = 1.304;
            leverComponent.root.position.z = -0.88;
            leverComponent.root.rotation.y = Math.PI;
            leverComponent.root.scale.setScalar(0.24);

            leverComponent.clipActions.forEach((action) => {
              action.setDuration(1);
              action.enabled = true;
              action.paused = true;
              action.time = 1;
              action.setEffectiveWeight(0);
              action.play();
            });

            leverComponent.outputs.collider = leverComponent.root;
            this.assets[assetsLength - 1] = [data, [modelNode, leverComponent]];

            this.initMaterialUpdates(this.decrease);
            this.initMaterialUpdates(this.increase);
            this.initVideo(this.decrease.target.component, true);
            this.initVideo(this.increase.target.component, true);
            this.result.ok.node.root.visible = false;

            this.increase.controllerBtn.root.position.set(5.176, 1.36, -0.968);
            this.decrease.controllerBtn.root.position.set(5.176, 1.28, -0.968);
            if (this.context.renderer.xr) this.getXRController();

            this.setSideCurlState('bad');
            this.setFrontCurlState('squished');

            resolve([modelNode, leverComponent]);
          } else if (state === LoadingState.Error) {
            resolve([] as any);
          }
        });
      } catch (e) {
        console.error(e);
        resolve([] as any);
      }
    });
  }

  initQuizes(data) {
    const { quizCompletedData, quiz1Data, quizEarlyData, quizLateData } = transformQuizData(data);

    const handleQuiz1StateChange = (newState) => {
      if (newState === 'success') this.active = true;
    };

    const handleQuizEarlyStateChange = (newState) => {
      if (newState === 'success' || newState === 'fail') this.active = true;
    };

    const handleQuizLateStateChange = (newState) => {
      if (newState === 'success' || newState === 'fail') {
        this.forceBlockResult = true;
        this.resetBtn.canBeInteracted = true;
        this.paperMachineVideoAssets.forEach((el) => {
          el.node.video.pause();
        });
      }
    };

    const handleQuizCompletedStateChange = (newState) => {
      if (newState === 'success') {
        if (this.context.renderer?.xr?.enabled && this.context.renderer?.xr?.isPresenting)
          this.context.renderer.xr.getSession().end();
        setTimeout(() => {
          this.startOutroVideo();
        }, 1000);
      }
    };

    this.quiz1 = new SceneQuiz(quiz1Data, handleQuiz1StateChange.bind(this));
    this.quizEarly = new SceneQuiz(quizEarlyData, handleQuizEarlyStateChange.bind(this));
    this.quizLate = new SceneQuiz(quizLateData, handleQuizLateStateChange.bind(this));
    this.quizCompleted = new SceneQuiz(
      quizCompletedData,
      handleQuizCompletedStateChange.bind(this),
    );
  }

  async createLever(modelNode) {
    const component = await modelNode.addComponent(gltfLoaderComponentType, {
      url: CONTROLLER_CONSTANTS.URL_LEVER,
      animationTriggerEnabled: true,
      animationEnabled: true,
      animationLoop: false,
    });

    await this.createIncreaseButton(modelNode);
    await this.createDecreaseButton(modelNode);
    component.onEvent = async (type: string) => {
      if (type === ComponentInteractionType.CLICK && this.active)
        this.emit(NodeEventsTypes.CONTROLLER_MODEL_CLICK, { data: this });
    };

    return component;
  }

  initVideo(component, startFromMiddle?) {
    if (component.node.video) {
      component.node.playBtn?.root.scale.setScalar(0);
      component.node.muteBtn?.root.scale.setScalar(0);

      const { video } = component.node;
      video.muted = true;
      video.playbackRate = 0;
      video.currentTime = startFromMiddle ? video.duration * 0.5 : 0;
      video.play();
    }
  }

  initMaterialUpdates(obj) {
    obj.target.component.node.plane.material.uniforms.tex.value.onUpdate = () => {
      obj.target.updateCount++;
      if (obj.target.updateCount === 2) {
        obj.controllerBtn.canBeInteracted = true;
      }
    };
  }

  async createIncreaseButton(modelNode) {
    const component = await modelNode.addComponent(controllerButtonType, {
      type: 'controller',
      size: 0.03,
      src: '/images/green_arrow_up.png',
      localPosition: {
        x: 0,
        y: 0,
        z: 0,
      },
      localRotation: {
        x: 0,
        y: Math.PI / 2,
        z: 0,
      },
    });
    this.increase.controllerBtn = component;
    this.initButtonEvents(this.increase, this.decrease);
  }

  async createDecreaseButton(modelNode) {
    const component = await modelNode.addComponent(controllerButtonType, {
      type: 'controller',
      size: 0.03,
      src: '/images/red_arrow_down.png',
      localPosition: {
        x: 0,
        y: 0,
        z: 0,
      },
      localRotation: {
        x: 0,
        y: Math.PI / 2,
        z: 0,
      },
    });
    this.decrease.controllerBtn = component;
    this.initButtonEvents(this.decrease, this.increase);
  }

  async createResetControllers(modelNode) {
    const resetBtn = await this.createResetButton3D(modelNode);
    const resetSwitch = await this.createResetSwitch3D(modelNode);
    return { resetBtn, resetSwitch };
  }

  async createResetButton3D(modelNode) {
    const button = await Button.create(
      this.context,
      modelNode,
      { url: CONTROLLER_CONSTANTS.URL_BUTTON },
      {
        onClick: () => {
          if (this.resetBtn.canBeInteracted) {
            this.resetBtn.active = true;
            this.resetBtn.canBeInteracted = false;
            this.resetSwitch.canBeInteracted = true;

            const hand = button.component.root.getObjectByName('Sketchfab_model');
            hand.visible = true;

            const actions = button.component.clipActions;
            actions.forEach((action) => {
              action.repetitions = 1;
              action.setEffectiveTimeScale(1).reset().play();
            });
          }
        },
        onLoaded: () => {
          const hand = button.component.root.getObjectByName('Sketchfab_model');
          hand.visible = false;
          setResetButtonPosition(button.component);
          const actions = button.component.clipActions;
          actions.forEach((action) => {
            action.setDuration(1);
            action.loop = this.THREE.LoopPingPong;
            action.clampWhenFinished = true;
            action.repetitions = 2;
            action.reset().play();
            action.paused = true;

            action.time = 0.2;
          });
        },
      },
    );

    return button;
  }

  async createResetSwitch3D(modelNode) {
    const resetSwitch = await Button.create(
      this.context,
      modelNode,
      { url: CONTROLLER_CONSTANTS.URL_SWITCH },
      {
        onClick: () => {
          if (resetSwitch.canBeInteracted) {
            resetSwitch.active = true;
            resetSwitch.canBeInteracted = false;
            this.forceBlockResult = false;
            this.paperMachineVideoAssets.forEach((el) => {
              el.node.video.play();
            });
            this.audio.volume = 0.2;
            this.audio.play();
            const action = resetSwitch.component.clipAction;
            action.loop = this.THREE.LoopPingPong;
            action.repetitions = 1;
            action.setEffectiveTimeScale(1).reset().play();
            setTimeout(() => {
              const hand = this.resetBtn.component.root.getObjectByName('Sketchfab_model');
              hand.visible = false;
              this.resetBtn.component.clipActions.forEach((el) => {
                el.paused = false;
                el.setEffectiveTimeScale(-1).reset().play();
              });
              action.paused = false;
              action.setEffectiveTimeScale(-1).reset().play();
            }, 3000);
            this.updatePlateVideoPlayback();
          }
        },
        onLoaded: () => {
          setResetSwitchPosition(resetSwitch.component);
          const action = resetSwitch.component.clipAction;
          action.setDuration(1);
          action.loop = this.THREE.LoopOnce;
          action.clampWhenFinished = true;
          action.repetitions = 2;
        },
      },
    );

    return resetSwitch;
  }

  onButtonIncreaseDragBegin() {
    this.UILeverController.buttonIncrease.button.scale.setScalar(0.9);
    this.UILeverController.buttonIncrease.inputs.active = true;
    this.increase.controllerBtn.inputs.active = true;
  }

  onButtonIncreaseDragEnd() {
    this.UILeverController.buttonIncrease.button.scale.setScalar(1);
    this.UILeverController.buttonIncrease.inputs.active = false;
    this.increase.controllerBtn.inputs.active = false;

    this.syncVideos(this.decrease, this.increase);
    this.decrease.controllerBtn.canBeInteracted = false;

    this.value =
      this.increase.target.component.node.video.currentTime /
      this.increase.target.component.node.video.duration;

    console.log(`Updating controller value: ${this.value}`);
    if (!this.forceBlockResult) {
      this.updatePlateVideoPlayback();
    }
  }

  onButtonDecreaseDragBegin() {
    this.UILeverController.buttonDecrease.button.scale.setScalar(0.9);
    this.UILeverController.buttonDecrease.inputs.active = true;
    this.decrease.controllerBtn.inputs.active = true;
  }

  onButtonDecreaseDragEnd() {
    this.UILeverController.buttonDecrease.button.scale.setScalar(1);
    this.UILeverController.buttonDecrease.inputs.active = false;
    this.decrease.controllerBtn.inputs.active = false;
    this.syncVideos(this.increase, this.decrease);
    this.increase.controllerBtn.canBeInteracted = false;

    this.value =
      1 -
      this.decrease.target.component.node.video.currentTime /
        this.decrease.target.component.node.video.duration;

    console.log(`Updating controller value: ${this.value}`);
    if (!this.forceBlockResult) {
      this.updatePlateVideoPlayback();
    }
  }

  async createUILeverController(modelNode) {
    const UILeverController = await UIController.create(
      this.context,
      this.xr,
      modelNode,
      () => {
        this.UILeverController.setVisible(false);
      },
      () => {
        this.updateControllers();
        this.animate();
      },
    );

    UILeverController.buttonIncrease.onEvent = async (type: string) => {
      if (!this.active) return;
      if (type === ComponentInteractionType.DRAG_BEGIN) {
        this.onButtonIncreaseDragBegin();
      }
      if (type === ComponentInteractionType.DRAG_END) {
        this.onButtonIncreaseDragEnd();
      }
    };

    UILeverController.buttonDecrease.onEvent = async (type: string) => {
      if (!this.active) return;
      if (type === ComponentInteractionType.DRAG_BEGIN) {
        this.onButtonDecreaseDragBegin();
      }
      if (type === ComponentInteractionType.DRAG_END) {
        this.onButtonDecreaseDragEnd();
      }
    };

    return UILeverController;
  }

  initButtonEvents(primary, secondary) {
    primary.controllerBtn.onEvent = async (type: string) => {
      if (type === ComponentInteractionType.DRAG_BEGIN && this.active) {
        if (this.active) primary.controllerBtn.root.scale.setScalar(0.9);
        primary.controllerBtn.inputs.active = true;
      }
      if (type === ComponentInteractionType.DRAG_END && this.active) {
        if (this.active) primary.controllerBtn.root.scale.setScalar(1);
        primary.controllerBtn.inputs.active = false;
        this.syncVideos(secondary, primary);
        secondary.controllerBtn.canBeInteracted = false;

        if (primary === this.increase)
          this.value =
            primary.target.component.node.video.currentTime /
            primary.target.component.node.video.duration;
        else
          this.value =
            1 -
            primary.target.component.node.video.currentTime /
              primary.target.component.node.video.duration;

        console.log(`Updating controller value: ${this.value}`);
        if (!this.forceBlockResult) {
          this.updatePlateVideoPlayback();
        }
      }
    };
  }

  getXRController() {
    const controller0 = this.context.renderer.xr.getController(0);
    const controller1 = this.context.renderer.xr.getController(1);

    const handleConnected = (controller, event) => {
      const handedness = event?.data?.handedness;
      if (handedness === 'left') {
        this.xr.controllerL = controller;
        this.xr.gamepadL = event.data.gamepad;
      } else if (handedness === 'right') {
        this.xr.controllerR = controller;
        this.xr.gamepadR = event.data.gamepad;
      }
    };

    const handleDisconnected = (controller, event) => {
      const handedness = event?.data?.handedness;
      if (handedness === 'left' && this.xr.controllerL === controller) {
        this.xr.controllerL = null;
        this.xr.gamepadL = null;
      } else if (handedness === 'right' && this.xr.controllerR === controller) {
        this.xr.controllerR = null;
        this.xr.gamepadR = null;
      }
    };

    controller0.addEventListener('connected', (e) => handleConnected(controller0, e));
    controller1.addEventListener('connected', (e) => handleConnected(controller1, e));
    controller0.addEventListener('disconnected', (e) => handleDisconnected(controller0, e));
    controller1.addEventListener('disconnected', (e) => handleDisconnected(controller1, e));
  }

  // Processing methods

  syncVideos(videoToSync, referenceVideo) {
    videoToSync.target.component.node.video.currentTime =
      referenceVideo.target.component.node.video.duration -
      referenceVideo.target.component.node.video.currentTime;
    referenceVideo.target.component.node.video.playbackRate = 0;
    this.resetTextureUpdateCount();
  }

  resetTextureUpdateCount() {
    this.decrease.target.updateCount = 0;
    this.increase.target.updateCount = 0;
  }

  hideSweeps(sweepsToHide) {
    this.mpSDK.Sweep.disable(...sweepsToHide);
  }

  setActive(value) {
    this.active = value;
  }

  updatePlateVideoPlayback() {
    if (this.forceBlockResult) return;
    if (this.value > CONTROLLER_CONSTANTS.ACCEPTED_VALUE_RANGE_UPPER) {
      this.forceBlockResult = true;
      this.quizEarly.setVisible(false);
      this.quizLate.resetAndShow();
      this.result.ok.node.root.visible = false;
      this.quizCompleted.setVisible(false);
      this.setSideCurlState('bad');
      this.setFrontCurlState('squished');
    }
    if (this.value < CONTROLLER_CONSTANTS.ACCEPTED_VALUE_RANGE_LOWER) {
      this.quizLate.setVisible(false);
      this.quizEarly.resetAndShow();
      this.result.ok.node.root.visible = false;
      this.quizCompleted.setVisible(false);
      this.setSideCurlState('bad');
      this.setFrontCurlState('squished');
    }
    if (
      this.value >= CONTROLLER_CONSTANTS.ACCEPTED_VALUE_RANGE_LOWER &&
      this.value <= CONTROLLER_CONSTANTS.ACCEPTED_VALUE_RANGE_UPPER
    ) {
      this.quizEarly.setVisible(false);
      this.quizLate.setVisible(false);
      this.result.ok.node.root.visible = true;
      this.quizCompleted.setVisible(true);
      this.setSideCurlState('good');
      this.setFrontCurlState('good');
    }
  }

  getAssetNode = (asset: any): TSceneNode | null => {
    let node = null;
    this.assets.forEach(([a, n]) => {
      if ((a.id && a.id === asset.id) || (a.uuid === asset.uuid && a.uuid && asset.uuid)) {
        node = n;
      }
    });
    return node;
  };

  setVideoVisibility(node, isVisible) {
    node.root.scale.y = isVisible ? 1 : 0;
  }

  //<editor-fold desc="Curl video visuals methods">
  setSideCurlState(state: 'good' | 'bad') {
    const upCurlBad = this.paperMachineVideoAssets.find(
      (asset) => asset.details.title === 'Up Curl - Bad',
    );
    const upCurlGood = this.paperMachineVideoAssets.find(
      (asset) => asset.details.title === 'Good Curl',
    );
    upCurlBad.node.root.visible = state === 'bad';
    upCurlGood.node.root.visible = state === 'good';
  }

  setFrontCurlState(state: 'good' | 'squished') {
    const frontOfPlates = this.paperMachineVideoAssets.find(
      (asset) => asset.details.title === 'Front of plates',
    );
    const frontOfPlatesSquished = this.paperMachineVideoAssets.find(
      (asset) => asset.details.title === 'Front of plates squished',
    );
    frontOfPlates.node.root.visible = state === 'good';
    frontOfPlatesSquished.node.root.visible = state === 'squished';
  }

  //</editor-fold>

  toggleVideoPlayback(node, shouldPlay) {
    node.video.playbackRate = shouldPlay ? 1 : 0;
  }

  isControllerButtonActive(controllerBtn) {
    return controllerBtn.inputs.active && controllerBtn.canBeInteracted;
  }

  isVideoNotNearEnd(node) {
    return node.video.currentTime < node.video.duration * 0.9;
  }

  //<editor-fold desc="Region frameLoop methods">
  animateLever(direction) {
    const animationUp = this.leverComponent.clipActions[1];
    const animationDown = this.leverComponent.clipActions[0];
    if (
      !animationUp ||
      !animationDown ||
      this.UILeverController?.lever.outputs.loadingState !== LoadingState.Loaded
    )
      return;

    if (direction === 'up') {
      this.UILeverController.animateUp();
      animateUp(animationUp, animationDown);
    } else if (direction === 'down') {
      this.UILeverController.animateDown();
      animateDown(animationUp, animationDown);
    } else {
      this.UILeverController.animateCenter();
      animateCenter(animationUp, animationDown);
    }
  }

  animate = async () => {
    const decreaseNode = this.decrease.target.component.node;
    const increaseNode = this.increase.target.component.node;
    if (
      (this.isControllerButtonActive(this.increase.controllerBtn) ||
        (this.xr.gamepadL?.axes[3] < -0.5 && this.increase.controllerBtn.canBeInteracted)) &&
      this.isVideoNotNearEnd(increaseNode)
    ) {
      this.setVideoVisibility(decreaseNode, false);
      this.setVideoVisibility(increaseNode, true);
      this.toggleVideoPlayback(increaseNode, true);
      this.animateLever('up');
    } else if (
      (this.isControllerButtonActive(this.decrease.controllerBtn) ||
        (this.xr.gamepadL?.axes[3] > 0.5 && this.decrease.controllerBtn.canBeInteracted)) &&
      this.isVideoNotNearEnd(decreaseNode)
    ) {
      this.setVideoVisibility(increaseNode, false);
      this.setVideoVisibility(decreaseNode, true);
      this.toggleVideoPlayback(decreaseNode, true);
      this.animateLever('down');
    } else {
      this.toggleVideoPlayback(increaseNode, false);
      this.toggleVideoPlayback(decreaseNode, false);
      this.animateLever('center');
    }
  };

  updateControllers() {
    if (
      this.xr.gamepadL?.axes[3] < -0.5 &&
      this.xr.gamepadLThumbstickDirection !== GamepadDirection.UP
    ) {
      this.onButtonIncreaseDragBegin();
      this.xr.gamepadLThumbstickDirection = GamepadDirection.UP;
    } else if (
      this.xr.gamepadL?.axes[3] >= 0.5 &&
      this.xr.gamepadLThumbstickDirection !== GamepadDirection.DOWN
    ) {
      this.onButtonDecreaseDragBegin();
      this.xr.gamepadLThumbstickDirection = GamepadDirection.DOWN;
    } else if (
      this.xr.gamepadL?.axes[3] <= 0.5 &&
      this.xr.gamepadL?.axes[3] >= -0.5 &&
      this.xr.gamepadLThumbstickDirection !== GamepadDirection.CENTER
    ) {
      if (this.xr.gamepadLThumbstickDirection == GamepadDirection.UP)
        this.onButtonIncreaseDragEnd();
      if (this.xr.gamepadLThumbstickDirection == GamepadDirection.DOWN)
        this.onButtonDecreaseDragEnd();

      this.xr.gamepadLThumbstickDirection = GamepadDirection.CENTER;
    }
  }

  //</editor-fold>
}

export default SceneController;
